import { isLocalOrchestratorHost } from './aiCaptainClient';

export type ReservationAiStreamType =
  | 'connected'
  | 'assistant_message'
  | 'reservation_confirmed'
  | 'error'
  | 'done';

export interface ReservationAiStreamMessage {
  type: ReservationAiStreamType;
  text?: string;
  speakText?: string;
  message?: string;
  choice?: Record<string, unknown>;
  reservation?: Record<string, unknown>;
}

export interface ReservationAiClientOptions {
  wsUrl: string;
  onMessage: (msg: ReservationAiStreamMessage) => void;
  onConnectionChange?: (connected: boolean) => void;
}

export interface ReservationAiSendPayload {
  type: 'start' | 'user_message';
  restaurantId: string;
  appName: string;
  customerId: string;
  userId: number;
  text?: string;
  context?: {
    restaurantName?: string;
    guestName?: string;
    guestPhone?: string;
    locationId?: number;
    locations?: { id: number; name: string }[];
    timeZone?: string;
  };
  token?: string;
}

const MAX_FLUSH_ATTEMPTS = 60;
const FLUSH_INTERVAL_MS = 50;
const RECONNECT_DELAY_MS = 5000;

function readAuthTokenFromCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|;\s*)authToken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

export class ReservationAiClient {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;
  private pendingQueue: ReservationAiSendPayload[] = [];
  private flushAttempts = 0;

  constructor(private options: ReservationAiClientOptions) {}

  connect(): void {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.intentionalClose = false;
    this.ws = new WebSocket(this.options.wsUrl);

    this.ws.onopen = () => {
      this.flushAttempts = 0;
      this.options.onConnectionChange?.(true);
      this.flushQueue();
    };

    this.ws.onclose = () => {
      this.ws = null;
      this.options.onConnectionChange?.(false);
      if (!this.intentionalClose) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      this.options.onConnectionChange?.(false);
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as ReservationAiStreamMessage;
        if (msg.type === 'connected') {
          this.options.onConnectionChange?.(true);
          return;
        }
        this.options.onMessage(msg);
      } catch {
        // ignore malformed frames
      }
    };
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.pendingQueue = [];
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.options.onConnectionChange?.(false);
  }

  send(payload: ReservationAiSendPayload): void {
    this.pendingQueue.push({
      ...payload,
      token: payload.token ?? readAuthTokenFromCookie(),
    });
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this.connect();
    }
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushAttempts = 0;
    this.flushTimer = setTimeout(() => this.flushQueue(), 0);
  }

  private flushQueue(): void {
    this.flushTimer = null;

    if (this.intentionalClose) return;

    if (this.ws?.readyState === WebSocket.OPEN) {
      while (this.pendingQueue.length > 0) {
        const next = this.pendingQueue.shift();
        if (next) {
          this.ws.send(JSON.stringify(next));
        }
      }
      this.flushAttempts = 0;
      return;
    }

    this.flushAttempts += 1;
    if (this.flushAttempts >= MAX_FLUSH_ATTEMPTS) {
      this.flushAttempts = 0;
      return;
    }

    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this.connect();
    }

    this.flushTimer = setTimeout(() => this.flushQueue(), FLUSH_INTERVAL_MS);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.intentionalClose) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.pendingQueue.length > 0 || !this.intentionalClose) {
        this.connect();
      }
    }, RECONNECT_DELAY_MS);
  }
}

export function getReservationAiWsUrl(): string {
  const fromEnv = process.env.REACT_APP_AI_RESERVATION_WS_URL;
  if (fromEnv) return fromEnv;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = isLocalOrchestratorHost(window.location.hostname)
    ? `${window.location.hostname}:8090`
    : window.location.host;
  return `${protocol}//${host}/ai/reservation/stream`;
}

const GUEST_ID_KEY = 'pepparReservationGuestId';

export function getReservationCustomerIdentity(userId: number | null | undefined): {
  customerId: string;
  userId: number;
} {
  if (userId && userId > 0) {
    return { customerId: String(userId), userId };
  }
  if (typeof localStorage === 'undefined') {
    return { customerId: 'guest-anonymous', userId: 0 };
  }
  let guestId = localStorage.getItem(GUEST_ID_KEY);
  if (!guestId) {
    guestId = `guest-${crypto.randomUUID()}`;
    localStorage.setItem(GUEST_ID_KEY, guestId);
  }
  return { customerId: guestId, userId: 0 };
}
